'use client';

import { UserRole, ROLE_LABELS, ROLE_COLORS } from '@/lib/rbac';

export type InvitationStatus = 'pending' | 'activated' | 'expired' | 'cancelled';

export interface StaffInvitation {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  farm: string;
  department: string;
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  status: InvitationStatus;
  activatedAt?: string;
  activationCode: string;
  notes?: string;
}

export const FARMS_LIST = [
  { id: 'farm-idudi', label: 'Idudi Main Farm' },
  { id: 'farm-jinja', label: 'Jinja Branch' },
  { id: 'farm-mbale', label: 'Mbale Outpost' },
  { id: 'farm-kampala', label: 'Kampala Office' },
];

export const DEPARTMENTS = [
  'Farm Operations',
  'Egg Production',
  'Health & Veterinary',
  'Feed Management',
  'Inventory & Stores',
  'Finance & Accounts',
  'Sales & Marketing',
  'Administration',
];

export const INVITATION_STATUS_STYLES: Record<InvitationStatus, { bg: string; text: string; label: string; dot: string }> = {
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Pending', dot: 'bg-amber-400' },
  activated: { bg: 'bg-green-50', text: 'text-green-700', label: 'Activated', dot: 'bg-green-500' },
  expired: { bg: 'bg-red-50', text: 'text-red-600', label: 'Expired', dot: 'bg-red-400' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Cancelled', dot: 'bg-gray-400' },
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  farm_owner: 'Full access to all modules, settings, and financial data across all farms.',
  manager: 'Manage operations, staff, approvals, and reports. Cannot change system settings.',
  technician: 'Record production data, manage health records, and view reports.',
  field_staff: 'Record daily activities, view assigned tasks, and log basic farm data.',
};

function randomCode(): string {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
}

export const MOCK_INVITATIONS: StaffInvitation[] = [
  {
    id: 'inv-001',
    email: 'amara.nakato@gmail.com',
    name: 'Amara Nakato',
    role: 'technician',
    farm: 'Idudi Main Farm',
    department: 'Egg Production',
    invitedBy: 'Okello James',
    invitedAt: '2026-08-28',
    expiresAt: '2026-09-11',
    status: 'pending',
    activationCode: 'TECH4892',
    notes: 'Replacing outgoing technician. Starts 10 Sep.',
  },
  {
    id: 'inv-002',
    email: 'david.opio@yahoo.com',
    name: 'David Opio',
    role: 'field_staff',
    farm: 'Jinja Branch',
    department: 'Farm Operations',
    invitedBy: 'Okello James',
    invitedAt: '2026-08-20',
    expiresAt: '2026-09-03',
    status: 'activated',
    activatedAt: '2026-08-22',
    activationCode: 'FLDS7731',
  },
  {
    id: 'inv-003',
    email: 'grace.auma@farm.ug',
    name: 'Grace Auma',
    role: 'manager',
    farm: 'Mbale Outpost',
    department: 'Farm Operations',
    invitedBy: 'Okello James',
    invitedAt: '2026-08-10',
    expiresAt: '2026-08-24',
    status: 'expired',
    activationCode: 'MGR2210',
    notes: 'Did not respond. Re-invite needed.',
  },
  {
    id: 'inv-004',
    email: 'peter.okwir@gmail.com',
    name: 'Peter Okwir',
    role: 'technician',
    farm: 'Idudi Main Farm',
    department: 'Health & Veterinary',
    invitedBy: 'Okello James',
    invitedAt: '2026-09-01',
    expiresAt: '2026-09-15',
    status: 'pending',
    activationCode: 'TECH9034',
  },
  {
    id: 'inv-005',
    email: 'sarah.atim@farm.ug',
    name: 'Sarah Atim',
    role: 'field_staff',
    farm: 'Kampala Office',
    department: 'Administration',
    invitedBy: 'Okello James',
    invitedAt: '2026-08-15',
    expiresAt: '2026-08-29',
    status: 'cancelled',
    activationCode: 'FLDS5512',
    notes: 'Position filled internally.',
  },
  {
    id: 'inv-006',
    email: 'moses.wafula@gmail.com',
    name: 'Moses Wafula',
    role: 'manager',
    farm: 'Jinja Branch',
    department: 'Finance & Accounts',
    invitedBy: 'Okello James',
    invitedAt: '2026-09-03',
    expiresAt: '2026-09-17',
    status: 'pending',
    activationCode: 'MGR6678',
  },
];

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function isExpiringSoon(expiresAt: string): boolean {
  const diff = new Date(expiresAt).getTime() - Date.now();
  return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000;
}

export { ROLE_LABELS, ROLE_COLORS };
